import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { FaUndo } from 'react-icons/fa';
import classnames from 'classnames';
import momentPropTypes from 'react-moment-proptypes';
import intlShape from '@/shared/utils/intlShape';
import { hasMultipleCompanies } from '@/shared/components/security/HasMultiCompany';
import CompanyMultiSelect from '@/shared/components/input/multiselect/CompanyMultiSelect';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import api from '@/shared/api';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import AuthService from '@/shared/services/AuthService';
import DataCacheService from '@/shared/services/DataCacheService';
import ClearButtonWrapper from '@/shared/components/ClearButtonWrapper';
import DelayedDateRangePicker from '@/shared/components/input/DelayedDateRangePicker';
import TeamMultiSelect from '@/shared/components/input/multiselect/TeamMultiSelect';
import PeriodButtonGroup, { getPeriodStartEndDates } from '@/shared/components/PeriodButtonGroup';
import UserSearch from '@/shared/components/input/UserSearch';
import guid from '@/shared/utils/guid';
import EndFiscalYearMultiSelect from '@/shared/components/input/multiselect/EndFiscalYearMultiSelect';
import config from '../../../config';

import messages from '../taskMessages';

const periods = ['all', 'month', 'nextMonth', 'quarter', 'nextQuarter', 'year'];

export const defaultFilters = {
  deadlineUntil: null,
  deadlineFrom: null,
  period: null,
  endFiscalYears: [],
  ownCompanies: [],
  userId: null,
  username: null,
  taskTemplateId: null,
  periodicityId: null,
  teams: [],
};

class TaskDashboardFilters extends Component {
  state = {
    templates: [],
    periodicities: [],
  };

  static propTypes = {
    onFilterChange: PropTypes.func.isRequired,
    filters: PropTypes.shape({
      deadlineUntil: momentPropTypes.momentObj,
      deadlineFrom: momentPropTypes.momentObj,
      endFiscalYears: PropTypes.arrayOf(PropTypes.string),
      period: PropTypes.oneOf(periods),
      ownCompanies: PropTypes.arrayOf(PropTypes.string),
      userId: PropTypes.string,
      taskTemplateId: PropTypes.string,
      periodicityId: PropTypes.string,
      teams: PropTypes.arrayOf(PropTypes.number),
    }),
    showPercentages: PropTypes.bool.isRequired,
    onChartTypeChange: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
  };

  static defaultProps = {
    filters: defaultFilters,
  };

  componentDidMount() {
    const { filters } = this.props;

    if (filters && filters.period) {
      this.setPeriod(filters.period);
    }
    if (filters && filters.endOfFiscalYearPeriod) {
      this.setEndOfFiscalYearPeriod(filters.endOfFiscalYearPeriod);
    }
    this.retrieveTemplates();
  }

  componentDidUpdate(prevProps) {
    const { filters: newFilters } = this.props;
    const { filters: oldFilters } = prevProps;

    if (oldFilters.taskTemplateId !== newFilters.taskTemplateId) {
      this.retrievePeriodicities(newFilters.taskTemplateId);
    }
  }

  filterChanged = filter => {
    const { filters, onFilterChange } = this.props;
    const newFilters = { ...filters, ...filter };

    onFilterChange(newFilters);
  };

  deadlineChanged = (startDate, endDate) => {
    this.filterChanged({ deadlineFrom: startDate, deadlineUntil: endDate, period: null });
  };

  endFiscalYearChanged = (dateFrom, dateUntil) => {
    this.filterChanged({
      endOfFiscalYearFrom: dateFrom,
      endOfFiscalYearUntil: dateUntil,
      endOfFiscalYearPeriod: null,
    });
  };

  setPeriod = value => {
    const [deadlineFrom, deadlineUntil, period] = getPeriodStartEndDates(value);
    this.filterChanged({ deadlineFrom, deadlineUntil, period });
  };
  setEndOfFiscalYearPeriod = value => {
    const [
      endOfFiscalYearFrom,
      endOfFiscalYearUntil,
      endOfFiscalYearPeriod,
    ] = getPeriodStartEndDates(value);
    this.filterChanged({ endOfFiscalYearFrom, endOfFiscalYearUntil, endOfFiscalYearPeriod });
  };
  periodChanged = ({ dateFrom, dateUntil, period }) => {
    this.filterChanged({ deadlineFrom: dateFrom, deadlineUntil: dateUntil, period });
  };

  endFiscalYearPeriodChanged = ({ dateFrom, dateUntil, period }) => {
    this.filterChanged({
      endOfFiscalYearFrom: dateFrom,
      endOfFiscalYearUntil: dateUntil,
      endOfFiscalYearPeriod: period,
    });
  };

  retrieveTemplates() {
    const { intl } = this.props;
    api.base.get(`${config.appUrls.tasks}/api/TaskTemplates`).then(({ data }) => {
      const translatedTemplates = data
        .map(t => ({
          ...t,
          label: intl.formatMessage(listItemMsg('taskTemplate', t.id, t.name)), // Map translation to label property
        }))
        .sort(dynamicSort('label')); // Sort on label property

      this.setState(
        {
          templates: translatedTemplates,
        },
        () => {
          const {
            filters: { taskTemplateId },
          } = this.props;
          if (taskTemplateId) {
            this.retrievePeriodicities(taskTemplateId);
          }
        },
      );
    });
  }

  templateChanged = event => {
    const taskTemplateId = event ? event.target.value : null;
    this.filterChanged({
      taskTemplateId,
      periodicityId: null,
    });

    this.retrievePeriodicities(taskTemplateId);
  };
  retrievePeriodicities = templateId => {
    const { intl } = this.props;
    if (!templateId) {
      this.setState({ periodicities: null });
      return;
    }
    return api.base
      .get(`${config.appUrls.tasks}/api/TaskPeriodicity/${templateId}`)
      .then(({ data }) => {
        const translatedPeriodicities = data
          .map(t => ({
            ...t,
            label: intl.formatMessage(listItemMsg('taskperiodicity', t.id, t.name)), // Map translation to label property
          }))
          .sort(dynamicSort('label'));

        this.setState({
          periodicities: translatedPeriodicities,
        });
      });
  };
  userChanged = item => {
    if (!item || !item.id) {
      this.filterChanged({ userId: null, username: null });
    } else {
      this.filterChanged({
        userId: item.id,
        username: item.name,
      });
    }
  };
  resetFilters = () => {
    const { onFilterChange } = this.props;
    onFilterChange({ ...defaultFilters });
  };

  render() {
    const { templates, periodicities } = this.state;
    const { filters, intl, showPercentages, onChartTypeChange } = this.props;
    const showCompanies = hasMultipleCompanies();

    const hasFilters = JSON.stringify(filters) !== JSON.stringify(defaultFilters);

    return (
      <div className="info-block container-fluid">
        <div className="row">
          <div className="col-md-3">
            <div className="form-group">
              <DelayedDateRangePicker
                startDate={filters.deadlineFrom}
                startDateId="TaskOverviewFilterDeadlineFrom"
                endDate={filters.deadlineUntil}
                endDateId="TaskOverviewFilterDeadlineUntil"
                onDatesChange={({ startDate, endDate }) => this.deadlineChanged(startDate, endDate)}
                startDatePlaceholderText={intl.formatMessage(messages.DeadlineFrom)}
                endDatePlaceholderText={intl.formatMessage(messages.DeadlineUntil)}
              />
            </div>
          </div>
          <div className="col-md-3 col-md-offset-6">
            <div className="form-group">
              <label className="margin5R margin 10T">
                <FormattedMessage {...messages.ChartType} />:
              </label>
              <div role="group" aria-label="crmType" className="btn-group pull-right">
                <button
                  onClick={() => onChartTypeChange(false)}
                  type="button"
                  title={intl.formatMessage(messages.ShowQuantitiesChart)}
                  className={classnames('btn btn-default', {
                    active: !showPercentages,
                  })}
                >
                  {intl.formatMessage(messages.Quantities)}
                </button>
                <button
                  onClick={() => onChartTypeChange(true)}
                  type="button"
                  title={intl.formatMessage(messages.ShowPercentagesChart)}
                  className={classnames('btn btn-default', {
                    active: showPercentages,
                  })}
                >
                  {intl.formatMessage(messages.Percentages)}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <PeriodButtonGroup
                dateFrom={filters.deadlineFrom}
                dateUntil={filters.deadlineFrom}
                period={filters.period}
                visiblePeriods={periods}
                onChange={this.periodChanged}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <TeamMultiSelect
                selection={filters.teams}
                onChange={teams => this.filterChanged({ teams })}
                idProp="code"
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-3 col-sm-6">
            <div className="form-group">
              {templates && (
                <ClearButtonWrapper
                  show={!!filters.taskTemplateId}
                  onClear={() => this.templateChanged(null)}
                >
                  <select
                    onChange={this.templateChanged}
                    className="form-control"
                    value={filters.taskTemplateId || ''}
                  >
                    <option value="">{intl.formatMessage(messages.AllTasks)}</option>
                    {templates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.label}
                      </option>
                    ))}
                  </select>
                </ClearButtonWrapper>
              )}
            </div>
          </div>
          <div className="col-md-3 col-sm-6">
            <div className="form-group">
              <ClearButtonWrapper
                show={!!filters.periodicityId}
                onClear={() => this.filterChanged({ periodicityId: null })}
              >
                <select
                  onChange={e => this.filterChanged({ periodicityId: e.target.value || null })}
                  className="form-control"
                  disabled={!periodicities || !periodicities.length}
                  value={filters.periodicityId || ''}
                >
                  <option value="">
                    {filters.periodicityId
                      ? intl.formatMessage(messages.ClearFilter)
                      : intl.formatMessage(messages.FilterPeriodicity)}
                  </option>
                  <option
                    style={{ fontSize: '1pt' }}
                    className="grey"
                    disabled="disabled"
                    value="&nbsp;"
                  >
                    &nbsp;
                  </option>
                  {periodicities
                    ? periodicities.map(periodicity => (
                        <option key={periodicity.id} value={periodicity.id}>
                          {periodicity.label}
                        </option>
                      ))
                    : null}
                </select>
              </ClearButtonWrapper>
            </div>
          </div>
          <div className="col-md-3 col-sm-6">
            <div className="form-group">
              <UserSearch
                placeholder={intl.formatMessage(messages.TasksAssignedTo)}
                onChange={this.userChanged}
                value={filters.userId}
                withCurrentUser
                withUnassigned
                unassignedId={guid.empty}
              />
            </div>
          </div>
          <div className="col-md-3 col-sm-6">
            {showCompanies && (
              <div className="form-group">
                <CompanyMultiSelect
                  selection={filters.ownCompanies}
                  onChange={companies => this.filterChanged({ ownCompanies: companies })}
                />
              </div>
            )}
          </div>
        </div>

        <div className="row">
          <div className="col-md-3 margin5B">
            <EndFiscalYearMultiSelect
              selection={filters.endFiscalYears}
              onChange={endFiscalYears => this.filterChanged({ endFiscalYears })}
            />
          </div>

          {hasFilters && (
            <div className="col-sm-4">
              <button
                type="button"
                className="btn btn-default btn-reset"
                onClick={() => this.resetFilters()}
              >
                <FaUndo className="react-icon" />
                &nbsp;
                <FormattedMessage {...messages.ResetFilters} />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default injectIntl(TaskDashboardFilters);
